import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check, Minus } from "lucide-react";
import { cn } from "./cn";

/**
 * A single yes/no choice, for the times a form needs an opt-in rather than a
 * pick-one. Stock shadcn themes this on `--primary`/`--background`; ours wears
 * the brand tokens, so a checkbox in the Studio's try-on changes colour with
 * everything else.
 *
 * The tick is `currentColor` via `text-primary-fg` rather than a hardcoded
 * white — a pale primary needs dark ink on it, and a fixed white tick would
 * vanish. Note `border-current` takes no opacity modifier (see the shadcn
 * skill), so the resting border names `border-border` outright.
 */
export const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      "peer h-4 w-4 shrink-0 rounded-[4px] border border-border bg-white",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
      "disabled:cursor-not-allowed disabled:opacity-50",
      "data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-fg",
      "data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-fg",
      className,
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
      {props.checked === "indeterminate" ? (
        <Minus className="h-3 w-3" aria-hidden />
      ) : (
        <Check className="h-3 w-3" aria-hidden />
      )}
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;
